import {
    LAST_ACTIVE_PROJECT_STORAGE_KEY,
    FREE_CANVAS_CREATIVE_BOOTSTRAP_MAX_AGE,
    CREATIVE_IMG_ALLOWED_RATIOS,
    CREATIVE_IMG_ALLOWED_COUNTS,
    DEFAULT_SLOT_PARAM_KEY_MAP,
    FUNCTION_SLOT_PARAM_KEY_MAP,
    MULTI_IMAGE_FUNCTION_KEYS,
    CANVAS_VERSION_CONFLICT_CODE,
} from '../constants/canvas';

export const cloneValue = (value) => {
    if (typeof structuredClone === 'function') return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
};

export const createUuid = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/[-_]/g, '');
    }

    return `${Date.now()}${Math.random().toString(16).slice(2)}`;
};

export const createNodeId = (nodeType) => `${nodeType}-${createUuid()}`;
export const createEdgeId = () => `edge-${createUuid()}`;

export const resolveExecutionIdentifier = (execution) => String(
    execution?.id
    || execution?.executionId
    || execution?.flowExecutionId
    || execution?.data?.id
    || execution?.data?.executionId
    || '',
);

export const isMultiImageFunctionKey = (functionKey) => MULTI_IMAGE_FUNCTION_KEYS.has(functionKey);

export const getSlotParamKey = (functionKey, slotKey) => (
    FUNCTION_SLOT_PARAM_KEY_MAP[functionKey]?.[slotKey]
    || DEFAULT_SLOT_PARAM_KEY_MAP[slotKey]
    || ''
);

export const getParamKeySlotMap = (functionKey) => Object.entries({
    ...DEFAULT_SLOT_PARAM_KEY_MAP,
    ...(FUNCTION_SLOT_PARAM_KEY_MAP[functionKey] || {}),
}).reduce((acc, [slotKey, paramKey]) => ({
    ...acc,
    [paramKey]: slotKey,
}), {});

export const inferFunctionKeyFromNodePatch = (patch = {}) => {
    if (patch?.functionKey) return String(patch.functionKey);

    const inputParams = (
        patch?.inputParams
        && typeof patch.inputParams === 'object'
        && !Array.isArray(patch.inputParams)
    ) ? patch.inputParams : {};

    if (Array.isArray(inputParams.imageUrls)) {
        if (
            Object.prototype.hasOwnProperty.call(inputParams, 'srcImage')
            || Object.prototype.hasOwnProperty.call(inputParams, 'refImage')
        ) {
            return 'templateReplace';
        }
        return 'creativeImg2Img';
    }

    if (
        Object.prototype.hasOwnProperty.call(inputParams, 'fastMode')
        && Object.prototype.hasOwnProperty.call(inputParams, 'clothType')
        && Object.prototype.hasOwnProperty.call(inputParams, 'mode')
    ) {
        return 'materialHD';
    }

    return '';
};

export const normalizeCreativeBootstrapPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.source !== 'creativeToImage') return null;

    const createdAt = Number(payload.createdAt || 0);
    const expiresAt = Number(payload.expiresAt || 0);
    const now = Date.now();
    if ((expiresAt && expiresAt < now) || (createdAt && now - createdAt > FREE_CANVAS_CREATIVE_BOOTSTRAP_MAX_AGE)) {
        return null;
    }

    const imageUrls = Array.isArray(payload.imageUrls)
        ? payload.imageUrls.filter(Boolean).slice(0, 14)
        : [];
    const prompt = String(payload.prompt || '');
    const aspectRatio = CREATIVE_IMG_ALLOWED_RATIOS.has(payload.aspectRatio) ? payload.aspectRatio : '3:4';
    const imageSize = payload.imageSize === '2K' ? '2K' : '4K';
    const nextNum = Number(payload.num || 4);
    const num = CREATIVE_IMG_ALLOWED_COUNTS.has(nextNum) ? nextNum : 4;

    return {
        prompt,
        imageUrls,
        aspectRatio,
        imageSize,
        num,
        shouldAutoGenerate: !!payload.shouldAutoGenerate && (prompt.trim().length > 0 || imageUrls.length > 0),
    };
};

export const buildCreativeBootstrapNodePatch = (payload) => {
    const imageUrls = Array.isArray(payload?.imageUrls) ? payload.imageUrls.filter(Boolean).slice(0, 14) : [];
    const prompt = String(payload?.prompt || '');
    const aspectRatio = CREATIVE_IMG_ALLOWED_RATIOS.has(payload?.aspectRatio) ? payload.aspectRatio : '3:4';
    const imageSize = payload?.imageSize === '2K' ? '2K' : '4K';
    const nextNum = Number(payload?.num || 4);
    const num = CREATIVE_IMG_ALLOWED_COUNTS.has(nextNum) ? nextNum : 4;
    const uploadItems = imageUrls.map((url) => ({ type: 'manual', url }));
    const uploads = imageUrls.map((url) => ({ url, source: 'upload' }));

    return {
        inputParams: {
            imageUrls: uploads,
            prompt,
            aspectRatio,
            imageSize,
            num,
        },
        panelState: {
            manualUploads: imageUrls,
            linkedUploads: [],
            uploadItems,
            uploads,
            prompt,
            model: 'NanoPro',
            aspectRatio,
            imageSize,
            num,
            skipSelect: false,
            materialSelections: [],
        },
    };
};

// 判断当前元素是否是可输入区域
export const isEditableTarget = (target) => {
    if (!(target instanceof HTMLElement)) return false;

    const tagName = target.tagName;
    return (
        target.isContentEditable
        || tagName === 'INPUT'
        || tagName === 'TEXTAREA'
        || tagName === 'SELECT'
        || !!target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')
    );
};

// 统一拦截输入态下的快捷键事件
export const isEditableEvent = (event) => {
    if (event?.isComposing) return true;
    if (isEditableTarget(event?.target) || isEditableTarget(document.activeElement)) return true;

    const activeElement = document.activeElement;
    if (
        activeElement instanceof HTMLElement
        && activeElement.closest('[data-free-canvas-panel-root="true"], .ant-modal, .ant-popover')
    ) {
        return true;
    }

    if (
        event?.target instanceof HTMLElement
        && event.target.closest('[data-free-canvas-panel-root="true"], .ant-modal, .ant-popover')
    ) {
        return true;
    }

    const eventPath = typeof event?.composedPath === 'function' ? event.composedPath() : [];
    return eventPath.some((item) => (
        isEditableTarget(item)
        || (item instanceof HTMLElement && item.closest('[data-free-canvas-panel-root="true"], .ant-modal, .ant-popover'))
    ));
};

// 工具切换前，判断是否需要避免打断面板输入
export const shouldBlockToolSwitchShortcut = () => {
    const activeElement = document.activeElement;
    if (
        activeElement instanceof HTMLElement
        && activeElement.closest(
            'input, textarea, select, [contenteditable="true"], [role="textbox"], [data-free-canvas-panel-root="true"], .ant-modal, .ant-popover',
        )
    ) {
        return true;
    }

    return !!document.querySelector(
        [
            '[data-free-canvas-panel-root="true"] textarea',
            '[data-free-canvas-panel-root="true"] input',
            '[data-free-canvas-panel-root="true"] [role="textbox"]',
            '.ant-modal textarea',
            '.ant-modal input',
            '.ant-modal [role="textbox"]',
        ].join(', '),
    );
};

export const copyTextToClipboard = async (text) => {
    if (!text) return false;

    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
    } catch (error) {
        document.body.removeChild(textarea);
        return false;
    }
};

export const toPositiveNumber = (value) => {
    const normalizedValue = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
    return Number.isFinite(normalizedValue) && normalizedValue > 0 ? normalizedValue : null;
};

export const sanitizeIdentifier = (value) => String(value ?? '').replace(/_/g, '');

export const toSerializableData = (value) => {
    if (value === undefined) return undefined;

    return JSON.parse(JSON.stringify(value, (key, currentValue) => (
        typeof currentValue === 'function' ? undefined : currentValue
    )));
};

export const getErrorMessage = (error, fallback = '操作失败') => error?.message || fallback;

export const isCanvasVersionConflictError = (error) => (
    Number(error?.code) === CANVAS_VERSION_CONFLICT_CODE
    || String(error?.message || '').includes('画布版本已变更')
);

export const getVersionNumber = (value) => {
    const version = Number(value ?? 0);
    return Number.isFinite(version) && version > 0 ? version : 0;
};

export const getStoredLastActiveProject = () => {
    if (typeof window === 'undefined') {
        return {
            projectId: '',
            pageIndex: 1,
        };
    }

    const rawValue = window.localStorage.getItem(LAST_ACTIVE_PROJECT_STORAGE_KEY) || '';
    if (!rawValue) {
        return {
            projectId: '',
            pageIndex: 1,
        };
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        return {
            projectId: typeof parsedValue?.projectId === 'string' ? parsedValue.projectId : '',
            pageIndex: Number.isFinite(Number(parsedValue?.pageIndex)) && Number(parsedValue.pageIndex) > 0
                ? Number(parsedValue.pageIndex)
                : 1,
        };
    } catch (error) {
        return {
            projectId: rawValue,
            pageIndex: 1,
        };
    }
};